import Component from "../abstracts/Component";
import vex from "vex-js";
import DatePickerComponent from "./DatePickerComponent";
import {
  trapFocusOnModal,
  removeTrapFocusOnModal,
  getFocusableElements
} from "../utils/TrapFocus";

export default class ModalComponent extends Component {
  get SELECTORS() {
    return {
      trigger: "[data-modal]",
      container: ".vex",
      datepicker: ".js-datepicker"
    };
  }

  get LIBRARIES_SELECTORS() {
    return {
      tab: "[data-tabs]",
      select: "select",
      fakeSelectToCleanUp:
        "select.select2-hidden-accessible + span.select2-container"
    };
  }

  get CLASSES() {
    return {
      canScroll: "can-scroll",
      clicked: "clicked"
    };
  }

  constructor() {
    super();
    this.datePickerComponent = new DatePickerComponent();
    this.loadedComponents = [];
    this.modalResizeObserver = null;
    this.trapFocusHandler = null;
    this.triggerElement = null;
  }

  /**
   * Setup ResizeObserver to watch for height changes in modal body
   * @param {jQuery} $container - The modal container element
   * @param {Function} callback - Callback function to execute on resize
   */
  observeModalBodyResize($container, callback) {
    // Find the modal body element
    const $modalBody = $container.find(".modal-body, .vex-content");

    if ($modalBody.length === 0) return;

    // Disconnect any existing observer
    this.disconnectModalResizeObserver();

    // Create new ResizeObserver
    this.modalResizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) {
        const { height } = entry.contentRect;

        // Execute callback with height information
        if (typeof callback === "function") {
          callback({
            element: entry.target,
            height: height,
            entry: entry
          });
        }

        // Emit custom event for other components to listen
        this.$emit(this.$customEvents.MODAL_EVENTS.heightChanged, {
          height: height,
          element: entry.target
        });
      }
    });

    // Start observing the modal body
    this.modalResizeObserver.observe($modalBody[0]);
  }

  /**
   * Disconnect the modal resize observer
   */
  disconnectModalResizeObserver() {
    if (this.modalResizeObserver) {
      this.modalResizeObserver.disconnect();
      this.modalResizeObserver = null;
    }
  }

  modalAfterOpen(vexInstance, closeText, overflow, ariaClose = "") {
    const ctx = this;
    let lang = this.datePickerComponent.getLocalization($("html").attr("lang"));
    this.datePickerComponent.initDatepicker(
      null,
      lang,
      this.SELECTORS.datepicker
    );

    let $close = $(vexInstance.closeEl);
    let $container = $(vexInstance.rootEl);
    let closeLabel = closeText;
    $($close).removeClass("vex-close");
    $($close).addClass("icon-close");

    if (closeText) {
      $close.text(closeLabel).addClass("cta-close");
    }
    if (ariaClose) {
      $close.attr("aria-label", ariaClose);
    }

    if (overflow && overflow === "auto") {
      $("body").addClass(this.CLASSES.canScroll);
    }

    let $tabs = $container.find(this.LIBRARIES_SELECTORS.tab);
    if ($tabs.length > 0) {
      this.moduleLoader.loadBootstrapJsComponentAndExecute(
        "tab",
        module => {
          $(this.LIBRARIES_SELECTORS.tab).tab();
          this.log("Tab initialized");
        },
        error => {
          console.log("failed to load tabs ", error.stack);
        }
      );
    }

    //ADD ACCESSIBILITY PROPERTIES
    $container.attr("tabindex", -1); //.attr('role', 'dialog');
    $close.attr("role", "button").attr("tabindex", 0);

    const firstFocusableEl = getFocusableElements(vexInstance.contentEl)[0];
    this.trapFocusHandler = trapFocusOnModal(vexInstance.contentEl);
    firstFocusableEl.focus();

    // Setup height change observer for modal body
    this.observeModalBodyResize($container, data => {
      // this.log(`Modal body height changed to: ${data.height}px`);
    });

    // FIX: BIA-5652 - WE UAT - Modale guida alle taglie nel flusso di cambio
    const modalContent = $container.find(".vex-content");
    const checkAdjustedClass = () => {
      modalContent.toggleClass("adjusted", modalContent.outerHeight() > window.innerHeight);
    };
    checkAdjustedClass();
    this.observeModalBodyResize($container, () => checkAdjustedClass());
    $(window).on("resize.modal-adjust", () => checkAdjustedClass());

    $close.on("click", () => {
      removeTrapFocusOnModal(vexInstance.contentEl, ctx.trapFocusHandler);
      this.triggerElement && this.triggerElement.focus();
      this.triggerElement = null;
    });

    $close.on("keydown", e => {
      if (e.key === "Enter" || e.key === " ") {
        $close.trigger("click");
      }
    });

    $container.on("keydown", e => {
      if (e.key === "Escape" || e.key === "Esc") {
        e.preventDefault();
        $close.trigger("click");
      }
    });
  }

  getRequestBody(el) {
    try {
      const body = el.data("body");
      return typeof body === "string" ? JSON.parse(el.data("body")) : body;
    } catch (e) {
      return {};
    }
  }

  openAjaxContent(el) {
    let ctx = this;
    let url = el.data("href") || el.attr("href");
    let title = el.data("title");
    let close = el.data("closeText");
    let ariaClose = el.data("ariaCloseText");
    let overlayClass = el.data("overlayClass");
    let overflow = el.data("overflow");
    let dataType = el.data("type");
    let dataEgonValidation = el.data("egonValidation");
    let modules = el.data("modules");
    let method = el.data("method") || "GET";
    let body = method.toUpperCase() === "POST" ? this.getRequestBody(el) : null;

    $.ajax({
      type: method,
      url: url,
      data: body,
      dataType: dataType,
      contentType: body ? "application/json; charset=utf-8" : undefined
    }).then(resp => {
      let chunked = $(resp).find("[data-ajax-chunk]");

      if (typeof resp != "object" && chunked.length) {
        resp = chunked.html();
      }

      vex.open({
        unsafeContent: this.wrappedContent(resp, title)[0].outerHTML,
        className: el.data("modalClass"),
        afterOpen: function() {
          setTimeout(function() {
            $("body").trigger("formInitializeAll");
          }, 1000);
          ctx.$emit(ctx.$customEvents.MODAL_EVENTS.afterOpen);
          ctx.modalAfterOpen(this, close, overflow, ariaClose);
          if (dataEgonValidation === true) {
            ctx.$emit(ctx.$customEvents.MODAL_EVENTS.egonValidation);
          }
          if (modules != undefined) {
            ctx.importDynamicComponents(modules);
          }
        },
        afterClose: function() {
          ctx.$emit(ctx.$customEvents.MODAL_EVENTS.afterClose, this);
          $("body").removeClass(ctx.CLASSES.canScroll);
          el.removeClass(ctx.CLASSES.clicked);
          // Disconnect the resize observer when modal closes
          ctx.disconnectModalResizeObserver();
        },
        overlayClassName: overlayClass
      });
    });
  }

  openStaticContent(el) {
    let ctx = this;
    let title = el.data("title");
    let close = el.data("closeText");
    let ariaClose = el.data("ariaCloseText");
    let content = $(el.data("modalId"))[0].outerHTML;

    vex.open({
      unsafeContent: this.wrappedContent(content, title)[0].outerHTML,
      className: el.data("modalClass"),
      afterOpen: function() {
        ctx.$emit(ctx.$customEvents.MODAL_EVENTS.afterOpen);
        ctx.modalAfterOpen(this, close, null, ariaClose);
      },
      afterClose: function() {
        ctx.$emit(ctx.$customEvents.MODAL_EVENTS.afterClose, this);
        $("body").removeClass(ctx.CLASSES.canScroll);
        el.removeClass(ctx.CLASSES.clicked);
        // Disconnect the resize observer when modal closes
        ctx.disconnectModalResizeObserver();
      }
    });
  }

  openRemoteContent(conf) {
    let title = $(conf.unsafeContent).data("title");
    let close = $(conf.unsafeContent).data("closeText");
    let ariaClose = $(conf.unsafeContent).data("ariaCloseText");
    const ctx = this;
    let classData = $(conf.unsafeContent).data("modalClass");
    if (conf.modalClass) {
      classData = conf.modalClass;
    }
    if (conf.canScroll) document.body.classList.add(this.CLASSES.canScroll);
    vex.open({
      unsafeContent: this.wrappedContent(conf.unsafeContent, title)[0]
        .outerHTML,
      className: classData,
      afterOpen: function() {
        ctx.$emit(ctx.$customEvents.MODAL_EVENTS.afterOpen);
        ctx.modalAfterOpen(this, close, null, ariaClose);
      },
      afterClose: function() {
        ctx.$emit(ctx.$customEvents.MODAL_EVENTS.afterClose, this);
        $("body").removeClass(ctx.CLASSES.canScroll);
        // Disconnect the resize observer when modal closes
        ctx.disconnectModalResizeObserver();
      }
    });
  }

  wrappedContent(content, title) {
    let container = $(`<div class="modal-content"></div>`);
    if (title) {
      let $header = $(
        `<div class="modal-header"><h2 class="h2-tertiary">${title}</div>`
      );
      container.append($header);
    } else {
      container.addClass("no-header");
    }
    if (content) {
      let $body = $(`<div class="modal-body">${content}</div>`);
      container.append($body);
    }
    return container;
  }

  importDynamicComponents(modulesList) {
    let ctx = this;
    this.loadedComponents = [];
    let modules = modulesList.split(",");
    if (modules.length > 0) {
      modules.forEach(function(module) {
        ctx
          .importComponents(module)
          .then(({ default: module }) => {
            if (!ctx.loadedComponents.includes(module)) {
              let component = new module(module);
              component.render();
              ctx.loadedComponents.push(module);
            }
          })
          .catch(error => {
            console.error("Failed to load the component", error.stack);
          });
      });
    }
  }

  importComponents(componentName) {
    return import(`../components/${componentName}`);
  }

  bindEvents() {
    let ctx = this;
    this.$on(
      "click.modal",
      ({ event }) => {
        event.preventDefault();
        if (event.currentTarget.classList.contains(ctx.CLASSES.clicked))
          return false;
        event.currentTarget.classList.add(ctx.CLASSES.clicked);
        this.triggerElement = event.currentTarget;
        let $el = $(event.currentTarget);
        if ($el.data("originalEvent") !== "run") {
          event.preventDefault();
        }
        if ($el.data("modalId")) {
          this.openStaticContent($el);
        } else {
          this.openAjaxContent($el);
        }
      },
      "body",
      this.SELECTORS.trigger
    );

    window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
        location.reload();
      }
    });
    
    this.$on(this.$customEvents.MODAL_EVENTS.show, ({ payload }) => {
      this.openRemoteContent(payload);
    });
  }

  render() {
    this.bindEvents();
  }
}
